import _ from 'lodash';
import {
  CALLBACK_PATH,
  CODE_CHALLENGE_METHOD,
  HOME_PATH,
  IGNORED_REDIRECT_PATHS,
  PROVIDER,
  RESPONSE_TYPES
} from '~/constants/_core/safea-v3';
import storage from '~/helpers/_core/storage';

const STATE_LENGTH = 16;
const CODE_VERIFIER_LENGTH = 32;

// OAuth2 helpers
function base64UrlEncode(str) {
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
}

function generateRandomBinaryString(length) {
  const bytes = window.crypto.getRandomValues(new Uint8Array(length));
  return String.fromCharCode(...bytes);
}

function generateState() {
  return base64UrlEncode(generateRandomBinaryString(STATE_LENGTH));
}

function generateNonce() {
  return base64UrlEncode(generateRandomBinaryString(STATE_LENGTH));
}

/**
 * NOTE: The code verifier SHOULD have enough entropy to make it
 *    impractical to guess the value.  It is RECOMMENDED that the output of
 *    a suitable random number generator be used to create a 32-octet
 *    sequence.  The octet sequence is then base64url-encoded to produce a
 *    43-octet URL safe string to use as the code verifier.
 * @returns {string}
 */
function generateCodeVerifier() {
  return base64UrlEncode(generateRandomBinaryString(CODE_VERIFIER_LENGTH));
}

/**
 * Stolen from https://developer.mozilla.org/en-US/docs/Web/API/SubtleCrypto/digest#converting_a_digest_to_a_hex_string
 */
async function generateCodeChallenge(codeVerifier) {
  const msgUint8 = new TextEncoder().encode(codeVerifier); // encode as (utf-8) Uint8Array
  const hashBuffer = await window.crypto.subtle.digest("SHA-256", msgUint8); // hash the message
  const hashArray = Array.from(new Uint8Array(hashBuffer)); // convert buffer to byte array

  // convert bytes to base64 string
  return base64UrlEncode(String.fromCharCode(...hashArray));
}

async function generateCodeChallengeAndVerifier() {
  const codeVerifier = generateCodeVerifier();
  return {
    codeVerifier,
    codeChallenge: await generateCodeChallenge(codeVerifier)
  };
}

// Safe A helpers
function getUserType(owner) {
  return owner === null ? 'operation' : 'tenant';
}

function getUserTypeV2(owner) {
  return owner === null ? 'userOperation' : 'userTenant';
}

function getClaimName(claim) {
  return `${PROVIDER.CLAIM_NAMESPACE}/${claim}`;
}

// API calls
const REQUIRED_SCOPES = ['openid', 'profile'];

function buildUrlSearchParams(params) {
  const mergedParams = _.transform(params, (result, value, key) => {
    if (typeof value === 'undefined') {
      return;
    }
    result[key] = value === null ? '' : value;
  });

  return new URLSearchParams(mergedParams);
}

function getCallbackUrl() {
  return `${window.location.origin}${process.env.APPLICATION_ROUTE_BASE}${CALLBACK_PATH}/`;
}

function buildSignOutUrl(params) {
  const mergedParams = { ...params };
  if (!mergedParams.post_logout_redirect_uri) {
    delete mergedParams.post_logout_redirect_uri;
  }

  return `${PROVIDER.SIGNOUT_ENDPOINT}?${buildUrlSearchParams(mergedParams).toString()}`;
}

const TRAILING_SLASH_REGEX = /\/$/;

function isSamePath(fullPath, comparePath) {
  try {
    const parsedUrl = new URL(fullPath, window.location.origin);
    return parsedUrl.pathname.replace(TRAILING_SLASH_REGEX, '') === comparePath.replace(TRAILING_SLASH_REGEX, '');
  } catch (err) {
    console.error(`[Cockpit] Invalid path "${fullPath}" === "${comparePath}"`, err);
    return fullPath.replace(TRAILING_SLASH_REGEX, '') === comparePath.replace(TRAILING_SLASH_REGEX, '');
  }
}

function isIgnoredRedirectPath(path) {
  return IGNORED_REDIRECT_PATHS.some((ignoredPath) => isSamePath(path, ignoredPath));
}

function buildRedirectUrl(redirectUrl) {
  return isIgnoredRedirectPath(redirectUrl) ? HOME_PATH : redirectUrl;
}

/**
 * @param {{
 *   code_challenge: string,
 *   scope: string,
 *   state: string,
 *   userType: 'operation' | 'tenant'
 * }} params
 * @returns {string}
 */
function _buildAuthorizeUrl(params) {
  return `${process.env.SAFEA_API_V3}/oauth2/authorize?${buildUrlSearchParams(params).toString()}`;
}

async function buildAuthorizeUrl({
  owner,
  tenantUuid,
  institutionUuid,
  hostname,
  returnUrl,
  v2RoleId,
  ltiLaunch,
  scopes = []
}) {
  const oauthState = generateState();
  const { codeChallenge, codeVerifier } = await generateCodeChallengeAndVerifier();
  storage.session.state = oauthState;
  storage.session.codeVerifier = codeVerifier;
  storage.session.returnUrl = buildRedirectUrl(returnUrl);

  const scope = [...REQUIRED_SCOPES, ...scopes];
  const userType = getUserType(owner);
  return _buildAuthorizeUrl({
    client_id             : process.env.SAFEA_CLIENT_ID,
    code_challenge        : codeChallenge,
    code_challenge_method : CODE_CHALLENGE_METHOD,
    scope                 : scope.join(' '),
    nonce                 : generateNonce(),
    state                 : oauthState,
    user_type             : userType,
    tenant_uuid           : tenantUuid,
    institution_uuid      : institutionUuid,
    redirect_uri          : getCallbackUrl(),
    hostname              : hostname,
    response_type         : RESPONSE_TYPES.CODE,
    lti_launch            : ltiLaunch,
    v2_role_id            : v2RoleId
  });
}

async function buildDefaultAuthorizeUrl({ store, route }, params = {}) {
  // Use the params from the full URL because it's simpler
  const url = new URL(window.location.href);
  url.searchParams.delete('lti_launch');
  url.searchParams.delete('tokenId');
  url.searchParams.delete('v2RoleId');
  url.searchParams.delete('tenantUuid');
  url.searchParams.delete('institutionUuid');

  // This is the route without params (e.g. /cockpit/my-account)
  const returnUrl = url.searchParams.get('returnUrl') ?? route.path + url.search;

  return await buildAuthorizeUrl({
    clientId        : process.env.SAFEA_CLIENT_ID,
    owner           : store.state._core.application.owner,
    tenantUuid      : store.state._core.tenant.uuid ?? void 0,
    institutionUuid : store.state._core.tenant.institution.uuid ?? void 0,
    ltiLaunch       : route.query.lti_launch ?? null,
    hostname        : url.hostname,
    returnUrl,
    ...params
  });
}

function clearAuthorizeUrlState() {
  storage.session.state = null;
  storage.session.codeVerifier = null;
  storage.session.returnUrl = null;
}

function mapUserInfoToUser(data, owner) {
  return {
    id                : data.sub,
    uuid              : data[getClaimName('user')].uuid,
    username          : data[getClaimName('user')].userName,
    name              : data.name,
    amr               : data.amr,
    email             : data.email,
    photo             : data.picture,
    timezone          : data.zoneinfo,
    type              : getUserTypeV2(owner),
    languageId        : data.locale,
    temporaryPassword : false,
    accessibility     : {
      darkThemeEnabled    : data[getClaimName('theme')] === 'dark',
      signLanguageEnabled : data[getClaimName('accessibility')]['signLanguageEnabled'] ?? false
    }
  };
}

export {
  buildAuthorizeUrl,
  buildDefaultAuthorizeUrl,
  buildSignOutUrl,
  isSamePath,
  isIgnoredRedirectPath,
  clearAuthorizeUrlState,
  generateCodeChallengeAndVerifier,
  generateState,
  getCallbackUrl,
  getClaimName,
  getUserType,
  getUserTypeV2,
  mapUserInfoToUser
};

